/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import {
    arrayOf,
    func,
    shape,
    string,
    bool,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import SearchClearIcon from '../../BrandTheme/Icons/SearchClearIcon';
import Pill from './Pill';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
    },
    rootDesktop: {
        alignItems: 'flex-start',
    },
    list: {
        display: 'flex',
        flex: 1,
        minWidth: 0,
        gap: '8px',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    listDesktop: {
        overflowX: 'visible',
        flexWrap: 'wrap',
    },
    listInlineMargin: {
        '& > *:first-child': {
            marginLeft: '16px',
        },
        '& > *:last-child': {
            marginRight: '16px',
        },
    },
    item: {
        flex: '0 0 auto',
    },
    endIconBtn: {
        border: 'none',
        background: 'transparent',
        padding: 0,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '16px',
        height: '16px',
        flexShrink: 0,
        lineHeight: 0,
        '& svg': {
            width: '9px',
            height: '9px',
        },
    },
    clearAll: {
        flexShrink: 0,
        border: 'none',
        background: 'transparent',
        textDecoration: 'underline',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        color: '#000000',
    },
    clearAllDesktop: {
        alignSelf: 'flex-start',
        marginTop: '8px',
    },
    clearAllMobile: {
        marginLeft: '8px',
    },
}));

const SelectedProductFacets = ({
    selectedFacets,
    selectedSortFacet,
    onRemove,
    onRemoveSort,
    onClearAll,
    hideClearAll,
    inlineMargin,
    loading,
    isDesktop,
}) => {
    const classes = useStyles();
    const chipItems = [
        ...(selectedSortFacet ? [{ ...selectedSortFacet, key: `sort-${selectedSortFacet.value}`, isSort: true }] : []),
        ...selectedFacets.map((facet) => ({ ...facet, key: facet.value, isSort: false })),
    ];

    if (!chipItems.length) {
        return null;
    }

    return (
        <div className={`${classes.root} ${isDesktop ? classes.rootDesktop : ''}`}>
            <div className={`${classes.list} ${isDesktop ? classes.listDesktop : ''} ${inlineMargin ? classes.listInlineMargin : ''}`}>
                {chipItems.map((facet) => (
                    <div className={classes.item} key={facet.key}>
                        <Pill
                            label={facet.label}
                            variant="chip"
                            endIcon={(
                                <button
                                    type="button"
                                    className={classes.endIconBtn}
                                    onClick={() => (facet.isSort ? onRemoveSort() : onRemove(facet.value))}
                                    aria-label={`Remove ${facet.label}`}
                                    disabled={loading}
                                >
                                    <SearchClearIcon fillColor="#000000" />
                                </button>
                            )}
                        />
                    </div>
                ))}
                {chipItems.length > 0 && !hideClearAll && !isDesktop && (
                    <button disabled={loading} type="button" className={`${classes.clearAll} ${classes.clearAllMobile}`} onClick={onClearAll}>
                        Clear all
                    </button>
                )}
            </div>
            {chipItems.length > 0 && !hideClearAll && isDesktop && (
                <button disabled={loading} type="button" className={`${classes.clearAll} ${classes.clearAllDesktop}`} onClick={onClearAll}>
                    Clear all
                </button>
            )}
        </div>
    );
};

SelectedProductFacets.propTypes = {
    selectedFacets: arrayOf(shape({
        label: string.isRequired,
        value: string.isRequired,
    })).isRequired,
    selectedSortFacet: shape({
        label: string.isRequired,
        value: string.isRequired,
    }),
    onRemove: func,
    onRemoveSort: func,
    onClearAll: func,
    hideClearAll: bool,
    inlineMargin: bool,
    loading: bool,
    isDesktop: bool,
};

SelectedProductFacets.defaultProps = {
    selectedSortFacet: null,
    onRemove: () => {},
    onRemoveSort: () => {},
    onClearAll: () => {},
    hideClearAll: false,
    inlineMargin: false,
    loading: false,
    isDesktop: false,
};

export default SelectedProductFacets;
